// Core
import PropTypes from "prop-types";

export const getInfoFromType = (type) => {
  const iconTypes = {
    COMMUNITY: { label: "business", color: "primary", url: () => "configuration" },
    MEMBER: { label: "account_circle", color: "info", url: (id) => `members/${id}` },
    RESOURCE: {
      label: "energy_savings_leaf",
      color: "success",
      url: () => "infrastructure/resources",
    },
    METER_CPE: {
      label: "electric_meter",
      color: "error",
      url: () => "infrastructure/meters",
    },
    METER_INTERNAL: {
      label: "electric_meter",
      color: "warning",
      url: () => "infrastructure/meters",
    },
    OWNER: {
      label: "account_circle",
      color: "info",
      url: () => "",
    },
  };

  return iconTypes[type] || { label: "account_circle", color: "info", url: () => "" };
};

/**
 * Filters a list of objects based on a specified type.
 *
 * @param {Array} list - The list of objects to be filtered.
 * @param {string} type - The type value to filter by.
 * @returns {Array} - Returns the filtered list of objects.
 */
export const filterByType = (list, type) => list.filter((item) => item.type === type);

export const organizationPropTypes = PropTypes.shape({
  tradingName: PropTypes.string.isRequired,
  // eslint-disable-next-line
  organizationChildRelationship: PropTypes.arrayOf(PropTypes.any),
  type: PropTypes.string,
  ownership: PropTypes.number,
  id: PropTypes.string,
  owners: PropTypes.arrayOf(
    PropTypes.shape({
      owner_id: PropTypes.string,
      owner_name: PropTypes.string,
      owner_perc: PropTypes.number,
    })
  ),
  responsibleMember: PropTypes.string,
  responsibleMemberId: PropTypes.string,
  contractId: PropTypes.string,
});

organizationPropTypes.organizationChildRelationship = PropTypes.arrayOf(organizationPropTypes);

/**
 * Generates an organizational structure from the community_structure API response.
 *
 * @param {Object} structureData - The API response from /community_structure.
 * @param {Object} community - The name and id of the community.
 * @returns {Object} - Returns a hierarchical structure for the Node/Organization tree.
 */
function buildResourceNode(resource) {
  return {
    tradingName: resource.resource_name,
    type: "RESOURCE",
    id: resource.resource_id,
    owners: resource.owners ?? [],
    organizationChildRelationship: [],
  };
}

function getCpeChildren(cpe) {
  if (cpe.internal_meters) {
    return cpe.internal_meters.map((im) => ({
      tradingName: im.meter_identifier,
      type: "METER_INTERNAL",
      id: null,
      organizationChildRelationship: im.resource ? [buildResourceNode(im.resource)] : [],
    }));
  }
  if (cpe.resource) {
    return [buildResourceNode(cpe.resource)];
  }
  return [];
}

export function generateOrganization(structureData, community) {
  return {
    tradingName: community.name,
    id: community.id,
    type: "COMMUNITY",
    organizationChildRelationship: (structureData?.cpes ?? []).map((cpe) => ({
      tradingName: cpe.meter_identifier,
      type: "METER_CPE",
      id: cpe.meter_id,
      contractId: cpe.current_contract_id ?? null,
      responsibleMember: cpe.current_responsible_member ?? null,
      responsibleMemberId: cpe.current_responsible_member_id ?? null,
      organizationChildRelationship: getCpeChildren(cpe),
    })),
  };
}

export default {
  getInfoFromType,
  filterByType,
  organizationPropTypes,
};
